import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  LogOut, Plus, Pencil, Trash2, Upload, X, Save, Image as ImageIcon,
  Loader2, Lock, Tag, Eye, EyeOff, ArrowLeft,
} from "lucide-react";
import client, { imgUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  title: "", category: "", cover_image: "", event_date: "", event_time: "",
  location: "", short_desc: "", full_content: "", gallery: [],
  link_text: "", link_url: "", status: "published", seo_title: "", meta_desc: "",
};

const field = "w-full rounded-lg bg-espresso-deep/40 border border-gold/20 px-4 py-2.5 text-cream text-sm font-sans placeholder:text-cream/30 focus:outline-none focus:border-gold/60 transition-colors";
const label = "block text-[11px] tracking-[0.2em] uppercase text-gold/70 mb-1.5";

// ---------------------------------------------------------------- Login
const Login = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) setErr(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-espresso-deep grain-overlay px-6" data-testid="admin-login">
      <form onSubmit={submit} className="relative w-full max-w-sm bg-[#1A100A] border border-gold/25 rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.5)] p-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-lg bg-gold/15 flex items-center justify-center"><Lock className="w-5 h-5 text-gold" /></span>
          <div>
            <h1 className="font-serif text-gold text-xl">Verwaltung</h1>
            <p className="text-[11px] text-cream/50 tracking-wide">Mara Wunderkammer</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className={label}>E-Mail</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className={field} placeholder="admin@marawunderkammer.ch" />
          </div>
          <div>
            <label className={label}>Passwort</label>
            <div className="relative">
              <input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className={field + " pr-11"} placeholder="••••••••" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/40 hover:text-gold">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-espresso-deep font-medium text-sm shadow-[0_0_22px_rgba(200,169,126,0.5)] hover:bg-gold-light transition-all disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Anmelden
          </button>
          <a href="/" className="block text-center text-xs text-cream/40 hover:text-gold transition-colors">Zur Website</a>
        </div>
      </form>
    </div>
  );
};

// ---------------------------------------------------------------- Image upload input
const ImageField = ({ value, onChange, testid }) => {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Bild hochgeladen");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testid}-input`} />
      {value ? (
        <div className="relative group rounded-lg overflow-hidden aspect-[16/10] bg-espresso-deep/40 border border-gold/20">
          <img src={imgUrl(value)} alt="" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-espresso-deep/80 text-cream flex items-center justify-center hover:bg-red-500/80 transition-colors"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testid}-btn`}
          className="w-full aspect-[16/10] rounded-lg border border-dashed border-gold/30 flex flex-col items-center justify-center gap-2 text-cream/50 hover:border-gold/60 hover:text-gold transition-colors">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
          <span className="text-xs">Bild hochladen</span>
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- Editor
const Editor = ({ initial, categories, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.category) {
      toast.error("Titel und Kategorie sind erforderlich");
      return;
    }
    setBusy(true);
    const payload = { ...form, event_date: form.event_date || null };
    try {
      if (initial?.id) await client.put(`/events/${initial.id}`, payload);
      else await client.post("/events", payload);
      toast.success("Beitrag gespeichert");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const addGallery = (url) => set("gallery", [...form.gallery, url]);
  const removeGallery = (i) => set("gallery", form.gallery.filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 z-[9500] bg-espresso-deep/80 backdrop-blur-sm overflow-y-auto" data-testid="event-editor">
      <div className="min-h-full flex items-start justify-center py-10 px-4">
        <form onSubmit={save} className="w-full max-w-3xl bg-[#1A100A] border border-gold/25 rounded-2xl shadow-2xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl text-gold">{initial?.id ? "Beitrag bearbeiten" : "Neuer Beitrag"}</h2>
            <button type="button" onClick={onClose} data-testid="editor-close" className="text-cream/50 hover:text-gold"><X className="w-5 h-5" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className={label}>Titel *</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="editor-title" className={field} />
            </div>

            <div>
              <label className={label}>Kategorie *</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="editor-category" className={field}>
                <option value="">— wählen —</option>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="editor-status" className={field}>
                <option value="published">Veröffentlicht</option>
                <option value="draft">Entwurf</option>
              </select>
            </div>

            <div>
              <label className={label}>Datum (optional)</label>
              <input type="date" value={form.event_date || ""} onChange={(e) => set("event_date", e.target.value)} data-testid="editor-date" className={field} />
            </div>
            <div>
              <label className={label}>Uhrzeit (optional)</label>
              <input value={form.event_time} onChange={(e) => set("event_time", e.target.value)} placeholder="z.B. 09:00 – 16:00" data-testid="editor-time" className={field} />
            </div>

            <div className="md:col-span-2">
              <label className={label}>Ort (optional)</label>
              <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Mara Wunderkammer, Basel" data-testid="editor-location" className={field} />
            </div>

            <div className="md:col-span-2">
              <label className={label}>Kurzbeschreibung</label>
              <textarea value={form.short_desc} onChange={(e) => set("short_desc", e.target.value)} rows={2} data-testid="editor-short" className={field} />
            </div>

            <div className="md:col-span-2">
              <label className={label}>Ausführlicher Text</label>
              <textarea value={form.full_content} onChange={(e) => set("full_content", e.target.value)} rows={6} data-testid="editor-content" className={field} />
            </div>

            <div className="md:col-span-2">
              <label className={label}>Titelbild</label>
              <ImageField value={form.cover_image} onChange={(v) => set("cover_image", v)} testid="editor-cover" />
            </div>

            <div className="md:col-span-2">
              <label className={label}>Galerie</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {form.gallery.map((g, i) => (
                  <div key={i} className="relative rounded-lg overflow-hidden aspect-square bg-espresso-deep/40 border border-gold/20">
                    <img src={imgUrl(g)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeGallery(i)} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-espresso-deep/80 text-cream flex items-center justify-center hover:bg-red-500/80"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <div className="aspect-square"><ImageField value="" onChange={addGallery} testid="editor-gallery" /></div>
              </div>
            </div>

            <div>
              <label className={label}>Button-Text (optional)</label>
              <input value={form.link_text} onChange={(e) => set("link_text", e.target.value)} placeholder="z.B. Auf Ricardo entdecken" data-testid="editor-link-text" className={field} />
            </div>
            <div>
              <label className={label}>Button-Link (optional)</label>
              <input value={form.link_url} onChange={(e) => set("link_url", e.target.value)} placeholder="https://…" data-testid="editor-link-url" className={field} />
            </div>

            <div>
              <label className={label}>SEO-Titel (optional)</label>
              <input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} data-testid="editor-seo" className={field} />
            </div>
            <div>
              <label className={label}>Meta-Beschreibung (optional)</label>
              <input value={form.meta_desc} onChange={(e) => set("meta_desc", e.target.value)} data-testid="editor-meta" className={field} />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8">
            <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-full border border-gold/30 text-cream/70 text-sm hover:border-gold hover:text-gold transition-colors">Abbrechen</button>
            <button type="submit" disabled={busy} data-testid="editor-save" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gold text-espresso-deep font-medium text-sm shadow-[0_0_22px_rgba(200,169,126,0.5)] hover:bg-gold-light transition-all disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Category manager
const CategoryManager = ({ categories, reload, onClose }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await client.post("/categories", { name });
      setName("");
      reload();
      toast.success("Kategorie hinzugefügt");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler");
    } finally {
      setBusy(false);
    }
  };
  const del = async (id) => {
    await client.delete(`/categories/${id}`);
    reload();
  };
  return (
    <div className="fixed inset-0 z-[9500] bg-espresso-deep/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="category-manager">
      <div className="w-full max-w-md bg-[#1A100A] border border-gold/25 rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-lg text-gold flex items-center gap-2"><Tag className="w-4 h-4" /> Kategorien</h2>
          <button onClick={onClose} className="text-cream/50 hover:text-gold"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Neue Kategorie" data-testid="cat-input" className={field} />
          <button onClick={add} disabled={busy} data-testid="cat-add" className="shrink-0 w-11 rounded-lg bg-gold text-espresso-deep flex items-center justify-center hover:bg-gold-light transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </button>
        </div>
        <ul className="space-y-2">
          {categories.map((c) => (
            <li key={c.id} className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-espresso-deep/40 border border-gold/15">
              <span className="text-sm text-cream/85">{c.name}</span>
              <button onClick={() => del(c.id)} data-testid={`cat-del-${c.name}`} className="text-cream/40 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Dashboard
const Dashboard = () => {
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | event
  const [showCats, setShowCats] = useState(false);

  const loadEvents = () => client.get("/events", { params: { include_drafts: true } }).then((r) => setEvents(r.data)).catch(() => {});
  const loadCats = () => client.get("/categories").then((r) => setCategories(r.data)).catch(() => {});

  useEffect(() => { loadEvents(); loadCats(); }, []);

  const remove = async (ev) => {
    if (!window.confirm(`„${ev.title}" wirklich löschen?`)) return;
    await client.delete(`/events/${ev.id}`);
    toast.success("Beitrag gelöscht");
    loadEvents();
  };

  return (
    <div className="min-h-screen bg-espresso-deep grain-overlay" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#1A100A]/95 backdrop-blur-xl border-b border-gold/15">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-gold text-lg">Verwaltung — Neuigkeiten</h1>
            <p className="text-[11px] text-cream/45">{user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href="/neuigkeiten" className="hidden sm:inline-flex items-center gap-1.5 text-xs text-cream/60 hover:text-gold px-3 py-2 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Zur Seite</a>
            <button onClick={() => setShowCats(true)} data-testid="open-categories" className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 px-4 py-2 text-xs text-cream/80 hover:border-gold hover:text-gold transition-colors"><Tag className="w-3.5 h-3.5" /> Kategorien</button>
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 px-4 py-2 text-xs text-cream/80 hover:border-red-400 hover:text-red-400 transition-colors"><LogOut className="w-3.5 h-3.5" /> Abmelden</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-serif text-2xl text-cream">Beiträge <span className="text-gold/60 text-lg">({events.length})</span></h2>
          <button onClick={() => setEditing({})} data-testid="new-event-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-espresso-deep font-medium text-sm shadow-[0_0_22px_rgba(200,169,126,0.5)] hover:bg-gold-light transition-all"><Plus className="w-4 h-4" /> Neuer Beitrag</button>
        </div>

        <div className="space-y-3" data-testid="admin-event-list">
          {events.length === 0 && <p className="text-cream/40 text-sm py-10 text-center">Noch keine Beiträge.</p>}
          {events.map((ev) => (
            <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="flex items-center gap-4 bg-[#1A100A] border border-gold/15 rounded-xl p-3 hover:border-gold/30 transition-colors">
              <div className="w-20 h-14 rounded-lg overflow-hidden bg-espresso-deep/50 shrink-0">
                {ev.cover_image ? <img src={imgUrl(ev.cover_image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-cream/20"><ImageIcon className="w-5 h-5" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wide bg-gold/15 text-gold px-2 py-0.5 rounded-full">{ev.category}</span>
                  {ev.status === "draft" && <span className="text-[10px] uppercase tracking-wide bg-cream/10 text-cream/60 px-2 py-0.5 rounded-full">Entwurf</span>}
                  {ev.archived && <span className="text-[10px] uppercase tracking-wide bg-cream/10 text-cream/50 px-2 py-0.5 rounded-full">Archiv</span>}
                </div>
                <h3 className="font-serif text-cream text-base truncate mt-1">{ev.title}</h3>
                {ev.event_date && <p className="text-[11px] text-cream/45">{ev.event_date}{ev.event_time ? ` · ${ev.event_time}` : ""}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setEditing(ev)} data-testid={`edit-${ev.id}`} className="w-9 h-9 rounded-lg text-cream/60 hover:text-gold hover:bg-gold/10 flex items-center justify-center transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(ev)} data-testid={`delete-${ev.id}`} className="w-9 h-9 rounded-lg text-cream/60 hover:text-red-400 hover:bg-red-400/10 flex items-center justify-center transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </main>

      {editing !== null && (
        <Editor initial={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadEvents(); }} />
      )}
      {showCats && <CategoryManager categories={categories} reload={loadCats} onClose={() => setShowCats(false)} />}
    </div>
  );
};

// ---------------------------------------------------------------- Page
const VerwaltungPage = () => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen bg-espresso-deep flex items-center justify-center"><Loader2 className="w-6 h-6 text-gold animate-spin" /></div>;
  return user ? <Dashboard /> : <Login />;
};

export default VerwaltungPage;
